import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { cardManagementService } from '../../../../services/cardManagementService';
import { clubEntryService } from '../../../../services/clubEntryService';
import salesService from '../../../../services/salesService';
import {
  Paper,
  Typography,
  Divider,
  Box,
  List,
  ListItem,
  Grid,
  Button,
  CircularProgress,
  Theme,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  useMediaQuery,
  useTheme,
  SwipeableDrawer,
  Backdrop,
  Select,
  MenuItem,
  FormControl,
  SelectChangeEvent,
  Chip,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ListAltIcon from '@mui/icons-material/ListAlt'; // ✅ YENİ: Sepetin tamamını gösteren "Tümü" butonu
import CloseIcon from '@mui/icons-material/Close';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import AppsIcon from '@mui/icons-material/Apps';
import DialpadIcon from '@mui/icons-material/Dialpad';
import HistoryIcon from '@mui/icons-material/History'; // ✅ YENİ: Geçmiş ikonu
import WaiterActionsHistoryModal from './modals/WaiterActionsHistoryModal'; // ✅ YENİ: Garson işlemleri geçmişi modalı
import { CartItem, Customer, FilteredProduct, InventoryCategory, QuickSaleScreenData } from '../QuickSaleScreen'; // PaymentOption çıkarıldı
import { PaymentOption } from './modals/QuickSaleScreenSettingsModal'; // PaymentOption buradan import edildi
import { PaymentDetails } from './modals/PartialPaymentModal';
import ConfirmCancelCheckModal from './modals/ConfirmCancelCheckModal'; // ✅ YENİ: Adisyon iptal onay modalı
import DeleteReasonModal from './modals/DeleteReasonModal'; // ✅ YENİ: İptal nedeni klavyeli giriş
import PavoLiveBadge from './PavoLiveBadge'; // ✅ YENİ: Rest-P2P cihaz canlılık rozeti
import ScanAlertBanner from './ScanAlertBanner'; // ✅ YENİ: Sepete eklenemeyen okutmaların uyarı şeridi
import type { ScanAlert } from '../hooks/useScanAlerts';
import { DEBUG_MODE } from '../constants';
import { useTranslation } from 'react-i18next';

// It's good practice to define props for styling functions if they become complex or reused.
// For sx props, we can directly use Theme from '@mui/material/styles' or import Theme from '@mui/material'

export interface CartSectionProps {
  cart: CartItem[];
  selectedCartItemId: string | null;
  handleSelectItem: (itemId: string) => void;
  // updateQuantity: (productId: string, change: number) => void; // Not directly used for rendering list, but for actions outside
  clearCart: () => void;
  calculateTotal: () => number;
  lastAppliedDiscountAmount: number;
  formatCurrency: (amount: number) => string;
  // appliedDiscountType: 'percentage' | 'amount' | null; // Not directly used in this JSX block for rendering logic other than discount display
  handleCashPaymentForQuickSale: () => Promise<void>;
  handlePosPaymentForQuickSale?: (bankAccountId: number, bankSubAccountId: number | null, bankAccountName: string, useFiscalDevice: boolean) => Promise<void>;
  handlePavoP2pPayment?: () => void;
  processPaymentAfterPartialModal: (paymentDetails: PaymentDetails) => Promise<void>;
  setIsPartialPaymentModalOpen: (isOpen: boolean) => void;
  isProcessingPayment: boolean;
  isProcessingPosPayment: boolean; // Sadece POS modal için
  cashPaymentLabel: string;
  singlePosOption: PaymentOption | null;
  posOptions: PaymentOption[];
  // openAccountPaymentOption: PaymentOption | null; // Not directly used by current buttons in this exact block
  showAlertModal: (title: string, message: string) => void;
  companyId: string | null; 
  currentScreen: QuickSaleScreenData | null;
  selectedCustomer: Customer | null; // CustomerData is aliased as Customer in QuickSaleScreen
  prepaidSystemEnabled?: boolean;
  prepaidCard?: any;
  selectedPrepaidWalletId?: number | null;
  onPrepaidPurchaseComplete?: () => void;
  // ✅ Ön ödemeli satışta CreatedBy olarak gönderilecek kullanıcı/kasiyer kimliği
  // (e-posta + kasiyer adı). Boşsa ekran ID'sine düşer — gün sonunda personel görünsün.
  prepaidCreatedBy?: string | null;
  // ✅ Ön ödemeli (kart) tamamlamada fiş yazdırma. Normal satışla aynı fonksiyon
  // kullanılır; içindeki "Yazarkasa ile Birlikte Fiş Yazıcısı Kullan" guard'ı
  // sayesinde ayar kapalıysa hiç basılmaz (normal satışla aynı kural).
  printReceiptAfterPayment?: (invoiceNumber: string, paymentType: string, items: any[], remainingBalance?: number, kitchenShortCode?: string | null, cardNumber?: string | null, pricesAreVatInclusive?: boolean) => Promise<void>;
  // ✅ Mutfak ekranı aktifse fişte sipariş no için rezerve edilmiş kısa kod
  reservedShortCode?: string | null;
  // ✅ Mutfak siparişinin rezerve numarayı kullanması için uzun kod (HS-YYYYMMDD-...).
  // Backend yalnızca ReservedLongCode + ReservedShortCode birlikte doluysa rezerveyi kullanır;
  // aksi halde yeni numara üretir (fiş ile mutfak no uyuşmazlığına yol açar).
  reservedLongCode?: string | null;
  allInventoryItems?: any[];
  screenSettings?: any;
  nextReceiptNumber?: number;
  showReceiptNumber?: boolean; // Fiş numarası gösterilsin mi?
  // MUI theme can be accessed via useTheme hook if needed for complex sx logic not covered by direct values
  onQuantityClick?: (item: CartItem) => void; // Adet kısmına tıklandığında çağrılacak fonksiyon
  // Mobil drawer içerikleri için (opsiyonel - şimdilik React.ReactNode olarak)
  mobileProductsContent?: React.ReactNode;
  mobileButtonsContent?: React.ReactNode;
  // ✅ YENİ: Restaurant Modu için
  isRestaurantMode?: boolean; // Restaurant modunda mı?
  tableName?: string; // Masa adı (modal için)
  waiterName?: string; // Garson adı (modal için)
  waiterCanCloseSale?: boolean; // Garson hesap kapatma yetkisi var mı?
  waiterCanCloseTable?: boolean; // Garson masa kapatma yetkisi var mı?
  waiterCanClearCart?: boolean; // Garson sepeti temizleme yetkisi var mı?
  waiterCanPrintToKitchen?: boolean; // Garson mutfağa yazdırma yetkisi var mı?
  waiterCanCancelReceipt?: boolean; // Garson adisyon iptal yetkisi var mı?
  waiterCanCancelUnprintedReceipt?: boolean; // ✅ YENİ: Yazdırılmamış adisyonu iptal yetkisi (masada yazdırılmış satır yoksa geçerli)
  waiterCanPrintReceipt?: boolean; // Garson adisyon yazdırma yetkisi var mı?
  useKitchenScreen?: boolean; // Mutfak ekranı ayarda aktif mi?
  onCloseTable?: (cancellationReason?: string) => void; // Masayı kapat fonksiyonu
  // Sentinel/nonce: dışarıdan iptal nedeni modalını tetiklemek için (son ürün
  // silme = adisyon iptal akışı). İlk render'da 0 olduğu için tetiklenmez; her
  // artıştaki yeni değer useEffect'i tetikler ve modal açılır.
  cancelReasonTriggerNonce?: number;
  onPrintToKitchen?: () => void; // Mutfağa yazdır fonksiyonu
  onPrintCheck?: () => void; // ✅ YENİ: Adisyon yazdır fonksiyonu
  isClosingTable?: boolean; // Masa kapatılıyor mu?
  // ✅ YENİ: Sepet bölme özellikleri
  hasSplitCart?: boolean; // Sepet bölünmüş mü?
  splitRightCart?: CartItem[]; // Sağ sepet (Sepet 2)
  selectedCartView?: 'cart1' | 'cart2'; // Şu an hangi sepet görüntüleniyor?
  onCartViewChange?: (view: 'cart1' | 'cart2') => void; // Sepet görünümü değiştiğinde
  // ✅ YENİ: Masa geçmişi
  waiterActionsJson?: string | null; // Garson işlemleri JSON
  tableId?: string; // Masa ID (geçmiş modalı için)
  // ✅ YENİ: Diğer ödeme yöntemleri modalı
  onOpenOtherPaymentModal?: () => void; // Diğer ödeme modalını aç
  hasOtherPaymentOptions?: boolean; // Diğer ödeme yöntemleri tanımlı mı?
  // ✅ YENİ: Online ödeme modalı
  onOpenOnlinePaymentModal?: () => void; // Online ödeme modalını aç
  hasOnlinePaymentOptions?: boolean; // Online ödeme yöntemleri tanımlı mı?
  // ✅ YENİ: Sepeti temizle fonksiyonu (ActionControlsPanel'e taşındı, ama dialog burada)
  onOpenClearCartConfirm?: () => void; // Sepeti temizleme onay dialogunu aç (dışarıdan)
  // ✅ YENİ: Menü opsiyon düzenleme
  onMenuOptionEdit?: (item: CartItem) => void; // Menü opsiyonlarını düzenle
  // ✅ YENİ: Paket hızlı satış modu
  isPackageMode?: boolean; // Paket modu aktif mi?
  onConfirmPackageCart?: () => void; // Paket sepetini onayla
  isPackageCustomerReady?: boolean; // ✅ KRİTİK: Paket müşteri bilgileri yüklendi mi?
  // Açık hesap ödeme
  handleOpenAccountPayment?: () => Promise<void>;
  openAccountPaymentOption?: PaymentOption | null;
  pavoP2pPaymentPending?: boolean;
  // ✅ Rest-P2P (pavo_rest_p2p) — NAKIT/POS/Karma butonlarının üstünde badge
  isRestP2PEnabled?: boolean;
  restP2PDeviceCode?: string | null;
  restP2PDeviceName?: string | null;
  // ✅ "Nakit ödemelerde ÖKC kullan" ayarı — KAPALI ise nakit butonunda Pavo
  // canlılık noktası gösterilmez ve nakit butonu Pavo offline olsa bile aktif kalır
  // (çünkü nakit Pavo'ya hiç gitmiyor).
  useFiscalDeviceForCash?: boolean;
  // ✅ YENİ: Okutulduğu hâlde sepete eklenemeyen ürünlerin uyarı şeridi
  scanAlerts?: ScanAlert[];
  onOpenScanAlerts?: () => void;
  onClearScanAlerts?: () => void;
  /**
   * ✅ YENİ: POS butonunun tıklama yolunu dışarıya verir. Local service'ten gelen
   * "POS ile kapat" kısayolu, butonun çağırdığı kodun birebir aynısını çağırabilsin
   * diye. Ödeme akışı buradan taşınmaz; yalnızca referansı paylaşılır.
   */
  onRegisterPosButtonClick?: (fn: () => void) => void;
}

const CartSection: React.FC<CartSectionProps> = ({
  cart,
  selectedCartItemId,
  handleSelectItem,
  clearCart,
  calculateTotal,
  lastAppliedDiscountAmount,
  formatCurrency,
  handleCashPaymentForQuickSale,
  handlePosPaymentForQuickSale,
  handlePavoP2pPayment,
  processPaymentAfterPartialModal,
  setIsPartialPaymentModalOpen,
  isProcessingPayment,
  isProcessingPosPayment, // Sadece POS modal için
  cashPaymentLabel,
  singlePosOption,
  posOptions,
  showAlertModal,
  companyId,
  currentScreen,
  selectedCustomer,
  prepaidSystemEnabled = false,
  prepaidCard = null,
  selectedPrepaidWalletId = null,
  onPrepaidPurchaseComplete,
  prepaidCreatedBy = null,
  printReceiptAfterPayment,
  reservedShortCode = null,
  reservedLongCode = null,
  allInventoryItems = [],
  screenSettings = null,
  nextReceiptNumber = 1,
  showReceiptNumber = false, // Varsayılan olarak gösterme
  onQuantityClick, // Adet kısmına tıklandığında çağrılacak fonksiyon
  mobileProductsContent, // Mobil ürünler drawer içeriği
  mobileButtonsContent, // Mobil butonlar drawer içeriği
  isRestaurantMode = false, // ✅ YENİ: Restaurant modu
  tableName = '', // ✅ YENİ: Masa adı
  waiterName = '', // ✅ YENİ: Garson adı
  waiterCanCloseSale = true, // ✅ YENİ: Varsayılan olarak yetki var
  waiterCanCloseTable = true, // ✅ YENİ: Varsayılan olarak yetki var
  waiterCanClearCart = true, // ✅ YENİ: Varsayılan olarak yetki var
  waiterCanPrintToKitchen = true, // ✅ YENİ: Varsayılan olarak yetki var
  waiterCanCancelReceipt = false, // ✅ YENİ: Varsayılan olarak yetki YOK (güvenlik)
  waiterCanCancelUnprintedReceipt = false, // ✅ YENİ: Varsayılan olarak yetki YOK (güvenlik)
  waiterCanPrintReceipt = false, // ✅ YENİ: Varsayılan olarak yetki YOK (güvenlik)
  useKitchenScreen = false, // ✅ YENİ: Mutfak ekranı varsayılan kapalı
  onCloseTable, // ✅ YENİ: Masayı kapat fonksiyonu
  cancelReasonTriggerNonce, // ✅ YENİ: Son ürün silme → adisyon iptal nedeni modalı tetikleyici
  onPrintToKitchen, // ✅ YENİ: Mutfağa yazdır fonksiyonu
  onPrintCheck, // ✅ YENİ: Adisyon yazdır fonksiyonu
  isClosingTable = false, // ✅ YENİ: Masa kapatılıyor mu?
  // ✅ YENİ: Sepet bölme özellikleri
  hasSplitCart = false,
  splitRightCart = [],
  selectedCartView = 'cart1',
  onCartViewChange,
  // ✅ YENİ: Masa geçmişi
  waiterActionsJson = null,
  tableId,
  // ✅ YENİ: Diğer ödeme yöntemleri modalı
  onOpenOtherPaymentModal,
  hasOtherPaymentOptions = false,
  // ✅ YENİ: Online ödeme modalı
  onOpenOnlinePaymentModal,
  hasOnlinePaymentOptions = false,
  // ✅ YENİ: Menü opsiyon düzenleme
  onMenuOptionEdit,
  // ✅ YENİ: Paket hızlı satış modu
  isPackageMode = false,
  onConfirmPackageCart,
  isPackageCustomerReady = false, // ✅ KRİTİK: Müşteri bilgileri yüklendi mi?
  handleOpenAccountPayment,
  openAccountPaymentOption = null,
  pavoP2pPaymentPending = false,
  isRestP2PEnabled = false,
  restP2PDeviceCode = null,
  restP2PDeviceName = null,
  useFiscalDeviceForCash = false,
  // ✅ YENİ: Sepete eklenemeyen okutmaların uyarı şeridi
  scanAlerts = [],
  onOpenScanAlerts,
  onClearScanAlerts,
  onRegisterPosButtonClick,
}) => {
  const { t } = useTranslation('salesSystem');
  // ✅ Rest-P2P: Pavo cihazı online mı? (offline olduğunda butonları disable edelim)
  const [restP2POnline, setRestP2POnline] = useState<boolean>(false);
  // 🔍 DEBUG: Paket/restoran modu render izleri — her render'da çalıştığı için
  // yalnızca DEBUG_MODE açıkken yazılır (Electron'da console dosyaya yansıtılıyor).
  if (DEBUG_MODE && isPackageMode) {
    console.log('🔍 [CartSection DEBUG]', { isPackageMode, isPackageCustomerReady, cartLength: cart.length });
  }
  if (DEBUG_MODE && isRestaurantMode) {
    console.log('🔍 [CartSection RENDER]', { isRestaurantMode, waiterCanCancelReceipt, waiterCanCancelUnprintedReceipt, cartLength: cart.length, isClosingTable });
  }
  
  // Mobil kontrol
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Mobil drawer state'leri
  const [productsDrawerOpen, setProductsDrawerOpen] = useState(false);
  const [buttonsDrawerOpen, setButtonsDrawerOpen] = useState(false);
  
  // Sepeti temizleme onay modalı için state
  const [clearCartConfirmOpen, setClearCartConfirmOpen] = useState(false);

  // ✅ YENİ: "Tümü" modalı — büyük çözünürlüklü ekranlarda sepet paneline yalnızca
  // bir iki satır sığdığı için sepetin tamamı uzun ve dar bir modalda gösterilir.
  const [allItemsOpen, setAllItemsOpen] = useState(false);
  
  // ✅ YENİ: Adisyon iptal onay modalı için state
  const [cancelCheckConfirmOpen, setCancelCheckConfirmOpen] = useState(false);
  
  // ✅ YENİ: İptal nedeni modalı için state
  const [cancellationReasonModalOpen, setCancellationReasonModalOpen] = useState(false);

  // ✅ YENİ: Adisyon Yazdır / Mutfağa Yazdır onay modalı için state.
  // Hangi yazdırma türünün onaylanacağını tutar (null = modal kapalı).
  const [printConfirmType, setPrintConfirmType] = useState<'kitchen' | 'check' | null>(null);

  // ✅ YENİ: Son ürün silme → adisyon iptal akışını dışarıdan tetikleme.
  // Parent (QuickSaleScreen) `cancelReasonTriggerNonce` artırdığında
  // (sentinel pattern), iptal nedeni modalını açıyoruz. İlk render'da
  // değer 0/undefined olduğu için tetiklenmez.
  useEffect(() => {
    if (cancelReasonTriggerNonce && cancelReasonTriggerNonce > 0) {
      console.log('[CartSection] 🔔 cancelReasonTriggerNonce arttı → CancellationReasonModal açılıyor', { cancelReasonTriggerNonce });
      setCancellationReasonModalOpen(true);
    }
  }, [cancelReasonTriggerNonce]);

  // ✅ YENİ: Garson işlemleri geçmişi modalı için state
  const [historyModalOpen, setHistoryModalOpen] = useState(false);

  // ✅ YENİ: Görüntülenecek sepeti belirle (bölünmüşse dropdown seçimine göre)
  const displayedCart = useMemo(() => {
    if (hasSplitCart && selectedCartView === 'cart2') {
      return splitRightCart;
    }
    return cart;
  }, [cart, splitRightCart, hasSplitCart, selectedCartView]);

  // ✅ YENİ: Masada mutfağa yazdırılmış satır var mı? (tüm sepet üzerinden)
  const hasPrintedItemsInCart = useMemo(
    () => cart.some(item => (item.printedQuantity || 0) > 0),
    [cart]
  );

  // ✅ YENİ: Efektif adisyon iptal izni.
  // - canCancelReceipt: her durumda iptal edebilir (mevcut yetki).
  // - canCancelUnprintedReceipt: yalnızca masada HİÇ yazdırılmış satır yoksa
  //   iptal edebilir. Bir satır bile mutfağa yazdırıldıysa mevcut yetki gerekir.
  const canCancelReceiptEffective =
    waiterCanCancelReceipt || (waiterCanCancelUnprintedReceipt && !hasPrintedItemsInCart);

  // ✅ YENİ: Görüntülenen sepet için toplam hesapla
  // ⚠️ TERMİNOLOJİ: 'included' = KDV dahil fiyat (KDV eklenmeyecek), 'excluded' = KDV hariç fiyat (KDV eklenecek)
  const displayedTotal = useMemo(() => {
    return displayedCart.reduce((total, item) => {
      const itemTotal = item.price * item.quantity;
      // vatStatus === 'included' → Fiyat KDV dahil → KDV eklenmeyecek
      // vatStatus === 'excluded' veya undefined → Fiyat KDV hariç → KDV eklenecek
      if (item.vatStatus === 'included') {
        return total + itemTotal;
      } else {
        const vatAmount = itemTotal * ((item.vatRate || 0) / 100);
        return total + itemTotal + vatAmount;
      }
    }, 0);
  }, [displayedCart]);

  // ✅ YENİ: Sepet görünümü değiştirme
  const handleCartViewChange = (event: SelectChangeEvent<'cart1' | 'cart2'>) => {
    if (onCartViewChange) {
      onCartViewChange(event.target.value as 'cart1' | 'cart2');
    }
  };

  // Sepeti temizleme onayı için dialog açma fonksiyonu
  const handleOpenClearCartConfirm = () => {
    if (cart.length === 0) return; // Sepet boşsa bir şey yapma
    setClearCartConfirmOpen(true);
  };

  // Sepeti temizleme onayı için dialog kapatma fonksiyonu (iptal)
  const handleCloseClearCartConfirm = () => {
    setClearCartConfirmOpen(false);
  };

  // Onay verildiğinde sepeti temizleme fonksiyonu
  const onConfirmClearCart = () => {
    clearCart(); // Ana sepeti temizle fonksiyonunu çağır
    setClearCartConfirmOpen(false); // Onay modalını kapat
  };

  // ✅ YENİ: Adisyon iptal onay modalı açma fonksiyonu
  const handleOpenCancelCheckConfirm = () => {
    console.log('[CartSection] 🔴 handleOpenCancelCheckConfirm çağrıldı', { cartLength: cart.length, isRestaurantMode });
    if (cart.length === 0) return;
    setCancelCheckConfirmOpen(true);
  };

  // ✅ YENİ: Adisyon iptal onay modalı kapatma fonksiyonu (vazgeç)
  const handleCloseCancelCheckConfirm = () => {
    console.log('[CartSection] 🔴 handleCloseCancelCheckConfirm çağrıldı (vazgeç)');
    setCancelCheckConfirmOpen(false);
  };

  // ✅ YENİ: Onay verildiğinde iptal nedeni modalını aç
  const onConfirmCancelCheck = () => {
    console.log('[CartSection] 🔴 onConfirmCancelCheck çağrıldı - ConfirmCancelCheckModal onaylandı!');
    console.log('[CartSection] 🔴 cancelCheckConfirmOpen kapatılıyor, cancellationReasonModalOpen 350ms sonra açılacak');
    setCancelCheckConfirmOpen(false);
    setTimeout(() => {
      console.log('[CartSection] 🔴 setTimeout tetiklendi - cancellationReasonModalOpen = true yapılıyor');
      setCancellationReasonModalOpen(true);
    }, 350);
  };

  // ✅ YENİ: İptal nedeni girildiğinde adisyonu iptal et
  const onConfirmCancellationReason = (reason: string) => {
    console.log('[CartSection] 🔴 onConfirmCancellationReason çağrıldı, reason:', reason);
    setCancellationReasonModalOpen(false);
    if (onCloseTable) {
      onCloseTable(reason);
    }
  };

  const onCancelCancellationReason = () => {
    console.log('[CartSection] 🔴 onCancelCancellationReason çağrıldı (iptal nedeni vazgeçildi)');
    setCancellationReasonModalOpen(false);
  };

  // ✅ YENİ: Adisyon Yazdır / Mutfağa Yazdır onay modalı fonksiyonları
  const handleOpenPrintConfirm = (type: 'kitchen' | 'check') => {
    if (cart.length === 0) return;
    console.log('[CartSection] 🖨️ Yazdırma onay modalı açılıyor:', type);
    setPrintConfirmType(type);
  };

  const handleClosePrintConfirm = () => {
    setPrintConfirmType(null);
  };

  const onConfirmPrint = () => {
    const type = printConfirmType;
    setPrintConfirmType(null);
    if (type === 'kitchen') {
      onPrintToKitchen?.();
    } else if (type === 'check') {
      onPrintCheck?.();
    }
  };

  // This function was inside the POS button onClick, moving it out for clarity or reuse if needed
  const handlePosButtonClick = async () => {
    if (cart.length === 0) {
      showAlertModal(t('cartSection.alerts.warning'), t('cartSection.alerts.cartRequiredForPayment'));
      return;
    }
    if (!companyId || !currentScreen || !selectedCustomer) {
      showAlertModal(t('cartSection.alerts.error'), t('cartSection.alerts.missingPaymentInfo'));
      return;
    }

    if (singlePosOption && singlePosOption.bankAccountId) {
      // SQL-based POS payment
      if (handlePosPaymentForQuickSale) {
        await handlePosPaymentForQuickSale(
          parseInt(singlePosOption.bankAccountId, 10),
          singlePosOption.subAccountId ? parseInt(singlePosOption.subAccountId, 10) : null,
          singlePosOption.label,
          true // ÖKC kullanımı ZORUNLU (POS ödemede fiziksel cihaz gerekli)
        );
      } else {
        // Fallback: Firebase-based (eski yöntem)
        const totalAmount = calculateTotal();
        const paymentDetails: PaymentDetails = {
          paidByCash: 0,
          paidByPosMap: { [singlePosOption.id]: totalAmount },
          paidByOpenAccount: 0,
          totalPaid: totalAmount,
          remainingAmount: 0,
          changeAmount: 0,
        };
        await processPaymentAfterPartialModal(paymentDetails);
      }
    } else if (singlePosOption && !singlePosOption.bankAccountId) {
      showAlertModal(t('cartSection.alerts.posConfigMissingTitle'), t('cartSection.alerts.posConfigMissingBody', { label: singlePosOption.label }));
    } else {
      setIsPartialPaymentModalOpen(true);
    }
  };

  // ✅ YENİ: POS kısayolu butonun kendi yolunu kullansın. Ref'e her render'da
  // güncel closure yazılır; aksi halde kısayol eski sepet/cari ile çalışırdı.
  const posButtonClickRef = useRef(handlePosButtonClick);
  posButtonClickRef.current = handlePosButtonClick;

  useEffect(() => {
    onRegisterPosButtonClick?.(() => { void posButtonClickRef.current(); });
  }, [onRegisterPosButtonClick]);

  const showOpenAccountButton = !!(
    openAccountPaymentOption?.enabled &&
    handleOpenAccountPayment &&
    selectedCustomer &&
    selectedCustomer.companyName !== 'Genel Cari'
  );

  const [isPurchaseProcessing, setIsPurchaseProcessing] = useState(false);

  const handleCompleteTransactionInPrepaidMode = useCallback(async () => {
    if (cart.length === 0) {
      showAlertModal(t('cartSection.alerts.warning'), t('cartSection.alerts.cartRequiredForPayment'));
      return;
    }

    if (!companyId || !currentScreen) {
      showAlertModal(t('cartSection.alerts.error'), t('cartSection.alerts.missingCompanyOrScreen'));
      return;
    }

    if (!prepaidCard) {
      showAlertModal(t('cartSection.alerts.error'), t('cartSection.alerts.cardSelectionRequired'));
      return;
    }

    const session = prepaidCard.activeSession;
    if (!session || session.status !== 'ACTIVE') {
      showAlertModal(t('cartSection.alerts.error'), t('cartSection.alerts.noActiveSession'));
      return;
    }

    // ✅ Sepeti ikiye ayır: ücretli kalemler + 0 TL "tanımlı ürün hakkı" (entitlement) kalemleri.
    // 0 TL satış purchaseFromCard ile yapılamaz; bunlar ayrı consume-entitlements ile işlenir.
    const entitlementItems = cart.filter((i: any) => i.isEntitlement);
    const paidItems = cart.filter((i: any) => !i.isEntitlement);
    const hasEntitlements = entitlementItems.length > 0;

    const totalAmount = Math.round(calculateTotal() * 100) / 100;
    // Entitlement yoksa eski davranış: tutar pozitif olmalı. Entitlement varsa 0 tutar serbest.
    if (totalAmount <= 0 && !hasEntitlements) {
      showAlertModal(t('cartSection.alerts.warning'), t('cartSection.alerts.cartAmountMustBePositive'));
      return;
    }

    // Cüzdan/bakiye yalnızca ücretli kısım için gerekli
    let wallet: any = null;
    if (totalAmount > 0) {
      const allWallets = prepaidCard.wallets || session.walletBalances || [];
      if (allWallets.length === 0) {
        showAlertModal(t('cartSection.alerts.error'), t('cartSection.alerts.noWalletFound'));
        return;
      }

      wallet = allWallets[0];
      if (selectedPrepaidWalletId) {
        const found = allWallets.find((w: any) => w.walletId === selectedPrepaidWalletId);
        if (found) wallet = found;
      }

      const walletBalance = wallet.balance || 0;
      if (walletBalance < totalAmount) {
        const formatted = new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format;
        showAlertModal(
          "Yetersiz Bakiye",
          `Seçili cüzdan bakiyesi yetersiz!\n\nSepet tutarı: ${formatted(totalAmount)}\nCüzdan bakiyesi (${wallet.walletName}): ${formatted(walletBalance)}\nEksik: ${formatted(totalAmount - walletBalance)}`
        );
        return;
      }
    }

    const resolveWarehouseId = (cartItem: any) => {
      const inventoryItem = allInventoryItems?.find((inv: any) => inv.id === cartItem.id);
      return inventoryItem?.warehouseId
        || screenSettings?.stockVisibility?.warehouses?.[0]
        || screenSettings?.StockVisibilityJson?.warehouses?.[0]
        || null;
    };

    setIsPurchaseProcessing(true);
    try {
      // 1) Ücretli kalemler varsa karttan tahsil et
      if (totalAmount > 0 && wallet) {
        const cartItemNames = paidItems.map(item => `${item.name} x${item.quantity}`).join(', ');
        const description = `Hızlı Satış - ${cartItemNames}`.substring(0, 500);

        const stockItems = paidItems.map(cartItem => {
          const inventoryItem = allInventoryItems?.find((inv: any) => inv.id === cartItem.id);
          const vatRate = inventoryItem?.vatRate || 0;
          // ⚠️ Cüzdandan KDV DAHİL (brüt) tutar düşülüyor (Amount=totalAmount). Bu yüzden
          // kalem birim fiyatını da KDV dahil gönder ve PriceType='VAT_INCLUDED' yap. Aksi
          // halde backend net fiyatı "olduğu gibi" saklar (PriceType saklanmaz) ve fiş
          // önizleme/fatura toplamları KDV hariç çıkıp tahsil edilen tutarla tutmaz.
          const grossUnitPrice = cartItem.vatStatus === 'included'
            ? cartItem.price
            : Math.round(cartItem.price * (1 + vatRate / 100) * 100) / 100;
          return {
            ProductId: cartItem.id,
            ProductName: cartItem.name,
            WarehouseId: resolveWarehouseId(cartItem),
            Quantity: cartItem.quantity,
            UnitPrice: grossUnitPrice,
            VatRate: vatRate,
            SctRate: inventoryItem?.sctRate || 0,
            PriceType: 'VAT_INCLUDED',
            HasRecipe: inventoryItem?.hasRecipe || (cartItem as any).hasRecipe || false,
            HasMenu: inventoryItem?.hasMenu || (cartItem as any).hasMenu || false,
            SelectedOptions: (cartItem as any).selectedOptions || null,
          };
        });

        await cardManagementService.purchaseFromCard({
          CardId: prepaidCard.id,
          WalletId: wallet.walletId,
          Amount: totalAmount,
          Description: description,
          ReferenceId: `QS-${currentScreen.id}-${Date.now()}`,
          ReferenceType: 'QUICK_SALE',
          CreatedBy: prepaidCreatedBy || currentScreen.id?.toString() || '',
          ScreenId: currentScreen.id?.toString() || '',
          BranchId: (currentScreen as any)?.branchId || null,
          Items: stockItems,
        });
      }

      // 2) 0 TL tanımlı ürün hakları varsa kullanımı işle (+ stok çıkışı backend'de)
      if (hasEntitlements) {
        const cardUID = prepaidCard.cardUID || prepaidCard.cardNumber;
        const consumeItems = entitlementItems.map((cartItem: any) => {
          const inventoryItem = allInventoryItems?.find((inv: any) => inv.id === cartItem.id);
          const whId = resolveWarehouseId(cartItem);
          return {
            productId: cartItem.id,
            quantity: cartItem.quantity,
            warehouseId: whId || undefined,
            vatRate: inventoryItem?.vatRate || cartItem.vatRate || 0,
          };
        });
        await clubEntryService.consumeEntitlements(cardUID, consumeItems, {
          screenId: currentScreen?.id ? parseInt(String(currentScreen.id), 10) : undefined,
          consumedBy: prepaidCreatedBy || currentScreen.id?.toString() || '',
        });
      }

      // 2.5) 🍽️ MUTFAK EKRANI ENTEGRASYONU - Ön Ödemeli Kart
      // Normal hızlı satış akışıyla (usePayment NAKİT / PaymentFlowModals "Diğer Ödeme")
      // birebir aynı koşul: mutfak ekranı aktif + QR menü seçili + restoran modu DEĞİL.
      // Sepet temizlenmeden ÖNCE çalışır (cart hâlâ dolu). Mutfak hatası satışı bozmaz.
      try {
        const useKitchenScreenLocal = (screenSettings as any)?.useKitchenScreen || false;
        const selectedQRMenuIdLocal = (screenSettings as any)?.selectedQRMenuId || null;

        if (useKitchenScreenLocal && selectedQRMenuIdLocal && !isRestaurantMode) {
          const siparisDetaylari = cart
            .filter((i: any) => !String(i.id).startsWith('DEPOSIT-'))
            .map((item: any) => {
              const inventoryItem = allInventoryItems?.find((inv: any) => inv.id === item.id);
              return {
                UrunId: item.id,
                UrunAdi: item.name,
                BirimFiyat: item.price,
                Miktar: item.quantity,
                ToplamFiyat: item.price * item.quantity,
                UrunResmi: inventoryItem?.imageUrl || null,
                Aciklama: null,
                CategoryId: inventoryItem?.categoryId || null,
                VatRate: inventoryItem?.vatRate || null,
              };
            });

          const kitchenOrderRequest = {
            QRMenuId: selectedQRMenuIdLocal,
            PersonelFirebaseUid: null,
            PersonelName: 'Hızlı Satış',
            ToplamTutar: calculateTotal(),
            SiparisDetaylari: siparisDetaylari,
            SiparisNotu: null,
            OdemeYontemi: 'Ön Ödemeli Kart',
            IslemId: null,
            DekontId: null,
            QuickSaleScreenId: currentScreen?.id ? parseInt(String(currentScreen.id), 10) : null,
            ReferenceNumber: `QS-${currentScreen.id}-${Date.now()}`,
            IsRestaurantMode: false,
            ReservedLongCode: reservedLongCode || null,
            ReservedShortCode: reservedShortCode || null,
            BranchId: (currentScreen as any)?.branchId || null,
          };

          const kitchenResponse = await salesService.createKitchenOrderFromQuickSale(kitchenOrderRequest);
          if (kitchenResponse.success) {
            console.log('[MUTFAK EKRANI] ✅ Ön ödemeli satış mutfağa gönderildi:', kitchenResponse.data?.shortCode);
          } else {
            console.error('[MUTFAK EKRANI] ❌ Ön ödemeli - mutfak gönderim başarısız:', kitchenResponse.message);
          }
        }
      } catch (kitchenError) {
        // Satış başarılı olduğu için kullanıcıya hata gösterme — sadece logla.
        console.error('[MUTFAK EKRANI] ❌ Ön ödemeli - mutfak entegrasyonu hatası:', kitchenError);
      }

      // 3) Fiş yazdır (sepet temizlenmeden ÖNCE). printReceiptAfterPayment kendi
      // içinde "Yazarkasa ile Birlikte Fiş Yazıcısı Kullan" ayarını kontrol eder;
      // kapalıysa hiçbir şey basmaz (normal satışla aynı kural). Yazdırma hatası
      // satışı bozmamalı — sadece loglanır.
      if (printReceiptAfterPayment) {
        try {
          const refNo = `QS-${currentScreen.id}-${Date.now()}`;
          const remainingBalance = wallet
            ? Math.round(((wallet.balance || 0) - totalAmount) * 100) / 100
            : undefined;
          const invoiceItems = cart.map((item: any) => {
            const lineTotal = (item.price || 0) * (item.quantity || 0);
            const inventoryItem = allInventoryItems?.find((inv: any) => inv.id === item.id);
            return {
              id: `qs-${item.id}-${Date.now()}`,
              itemId: item.id,
              itemName: item.name,
              quantity: item.quantity,
              unit: item.unit || 'Adet',
              unitPrice: item.price,
              vatRate: inventoryItem?.vatRate || item.vatRate || 0,
              sctRate: 0,
              discountRate: 0,
              lineTotal,
              netTotal: lineTotal,
              vatAmount: 0,
              sctAmount: 0,
              grossTotal: lineTotal,
            };
          });
          // Kart no fişte gösterilsin; reservedShortCode varsa sipariş no basılır;
          // fiyatlar KDV dahil olduğundan KDV yeniden ekleme atlanır (390 → 420 olmasın).
          const prepaidCardNumber = prepaidCard?.cardNumber || prepaidCard?.cardUID || null;
          await printReceiptAfterPayment(refNo, 'ÖN ÖDEMELİ KART', invoiceItems, remainingBalance, reservedShortCode, prepaidCardNumber, true);
        } catch (printErr) {
          console.error('[Prepaid] Fiş yazdırma hatası (satış tamamlandı):', printErr);
        }
      }

      if (onPrepaidPurchaseComplete) {
        onPrepaidPurchaseComplete();
      }
    } catch (err: any) {
      const message = err.response?.data?.message || 'Karttan ödeme alınırken hata oluştu';
      showAlertModal(t('cartSection.alerts.error'), message);
    } finally {
      setIsPurchaseProcessing(false);
    }
  }, [cart, companyId, currentScreen, prepaidCard, selectedPrepaidWalletId, prepaidCreatedBy, calculateTotal, showAlertModal, onPrepaidPurchaseComplete, printReceiptAfterPayment, reservedShortCode, reservedLongCode, allInventoryItems, screenSettings, isRestaurantMode]);

  // ✅ YENİ: Sepet tablosu iki yerde çizilir: sağdaki panel ve "Tümü" modalı.
  // `large` sürümü modal içindir — büyük çözünürlüklü ekranlarda panele bir iki
  // satır sığdığı için modalda yazı tipleri belirgin şekilde büyütülür.
  const renderCartTableHeader = (large = false) => {
    const headerFont = large ? '0.95rem' : (isPackageMode ? '0.65rem' : '0.75rem');
    const useShort = isPackageMode && !large;
    return (
      <ListItem
        sx={{
          bgcolor: 'grey.200',
          py: large ? 1 : 0.5,
          borderBottom: '2px solid #ccc',
          position: 'sticky',
          top: 0,
          zIndex: 1
        }}
        disablePadding
      >
        <Grid container alignItems="center" sx={{ px: 1 }}>
          <Grid item xs={5}><Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: headerFont }}>{useShort ? t('cartSection.headers.productShort') : t('cartSection.headers.product')}</Typography></Grid>
          <Grid item xs={2} sx={{ textAlign: 'center' }}><Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: headerFont }}>{useShort ? t('cartSection.headers.quantityShort') : t('cartSection.headers.quantity')}</Typography></Grid>
          <Grid item xs={2} sx={{ textAlign: 'right' }}><Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: headerFont }}>{useShort ? t('cartSection.headers.unitPriceShort') : t('cartSection.headers.unitPrice')}</Typography></Grid>
          <Grid item xs={3} sx={{ textAlign: 'right' }}><Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: headerFont }}>{useShort ? t('cartSection.headers.totalShort') : t('cartSection.headers.total')}</Typography></Grid>
        </Grid>
      </ListItem>
    );
  };

  const renderCartRow = (item: CartItem, index: number, large = false) => {
    // ✅ FIX: cartItemId yoksa benzersiz bir fallback ID oluştur
    const itemKey = item.cartItemId || `fallback-${item.id}-${index}`;
    const nameFont = large ? '1.25rem' : (isPackageMode ? '0.8rem' : '0.95rem');
    const subFont = large ? '0.9rem' : '0.7rem';
    const qtyFont = large ? '1.3rem' : (isPackageMode ? '0.8rem' : '0.95rem');
    const priceFont = large ? '1.2rem' : (isPackageMode ? '0.75rem' : '0.95rem');
    const totalFont = large ? '1.35rem' : (isPackageMode ? '0.8rem' : '1.05rem');

    return (
      <ListItem
        key={itemKey}
        disablePadding
        sx={theme => {
          // ✅ YENİ: Reçete/Menü durumuna göre arka plan rengi
          let bgColor = 'transparent';
          let borderLeft = 'none';
          if (selectedCartItemId === itemKey) {
            bgColor = theme.palette.action.selected;
          } else if (item.hasRecipe && item.hasMenu) {
            // Hem reçete hem menü var - mor ton
            bgColor = 'rgba(156, 39, 176, 0.08)'; // #9c27b0 purple
            borderLeft = '3px solid #9c27b0';
          } else if (item.hasRecipe) {
            // Sadece reçete var - yeşil ton
            bgColor = 'rgba(76, 175, 80, 0.08)'; // #4caf50 green
            borderLeft = '3px solid #4caf50';
          } else if (item.hasMenu) {
            // Sadece menü var - mavi ton
            bgColor = 'rgba(33, 150, 243, 0.08)'; // #2196f3 blue
            borderLeft = '3px solid #2196f3';
          }

          return {
            borderBottom: '1px solid #eee',
            cursor: 'pointer',
            backgroundColor: bgColor,
            borderLeft: borderLeft,
            '&:hover': {
              backgroundColor: selectedCartItemId !== itemKey ? theme.palette.action.hover : undefined,
            },
            py: large ? 1.5 : (isPackageMode ? 0.25 : 1) // ✅ Paket modunda kompakt, modalda ferah
          };
        }}
        onClick={() => handleSelectItem(itemKey)}
      >
        <Grid container alignItems="center" sx={{ px: 1 }}>
          <Grid item xs={5}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 'medium',
                fontSize: nameFont,
                // ✅ YENİ: Reçete/Menü durumuna göre ikon
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              {item.name}
              {item.hasRecipe && (
                <span title="Reçeteli Ürün" style={{ color: '#4caf50', fontSize: subFont }}>🥗</span>
              )}
              {item.hasMenu && (
                <span title="Menü Ürünü" style={{ color: '#2196f3', fontSize: subFont }}>📋</span>
              )}
              {item.note && (
                <span title={item.note} style={{ color: '#ff9800', fontSize: subFont }}>📝</span>
              )}
            </Typography>
            {item.note && (
              <Typography variant="caption" sx={{ color: 'warning.main', fontStyle: 'italic', fontSize: subFont, pl: 0.5 }}>
                {item.note}
              </Typography>
            )}
            {/* ✅ YENİ: Menü alt ürünleri (normal ve opsiyonlar) */}
            {item.hasMenu && (
              <Box
                sx={{
                  pl: 2,
                  mt: 0.5,
                  cursor: item.selectedOptions && item.selectedOptions.length > 0 ? 'pointer' : 'default',
                  '&:hover': item.selectedOptions && item.selectedOptions.length > 0
                    ? { bgcolor: 'rgba(33, 150, 243, 0.1)' }
                    : {},
                  borderRadius: 1,
                  py: 0.25
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  // Sadece opsiyonlar varsa düzenleme modalı aç
                  if (item.selectedOptions && item.selectedOptions.length > 0 && onMenuOptionEdit) {
                    onMenuOptionEdit(item);
                  }
                }}
              >
                {/* Normal menü öğeleri */}
                {item.menuNormalItems && item.menuNormalItems.length > 0 && (
                  <>
                    {item.menuNormalItems.map((normalItem, nIndex) => (
                      <Typography
                        key={`n-${nIndex}`}
                        variant="caption"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          color: 'text.secondary',
                          fontSize: subFont
                        }}
                      >
                        <span style={{ color: '#4caf50' }}>•</span>
                        {normalItem.quantity > 1 ? `${normalItem.quantity} ${normalItem.unit} ` : ''}{normalItem.itemName}
                      </Typography>
                    ))}
                  </>
                )}
                {/* Seçilen opsiyonlar */}
                {item.selectedOptions && item.selectedOptions.length > 0 && (
                  <>
                    {item.selectedOptions.map((opt, optIndex) => (
                      <Typography
                        key={`o-${optIndex}`}
                        variant="caption"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          color: 'text.secondary',
                          fontSize: subFont
                        }}
                      >
                        <span style={{ color: '#2196f3' }}>↳</span>
                        {opt.itemName || opt.optionGroupName}
                        {opt.selectedExtraOption && (
                          <span style={{ color: '#ff9800' }}>
                            → {opt.selectedExtraOption.optionItemName} (+{opt.selectedExtraOption.priceDifference}₺)
                          </span>
                        )}
                      </Typography>
                    ))}
                    {item.totalPriceDifference && item.totalPriceDifference > 0 && (
                      <Typography
                        variant="caption"
                        sx={{ color: 'info.main', fontWeight: 'bold', fontSize: subFont }}
                      >
                        Ekstra: +{item.totalPriceDifference.toFixed(2)}₺
                      </Typography>
                    )}
                  </>
                )}
              </Box>
            )}
          </Grid>
          <Grid item xs={2} sx={{ textAlign: 'center' }}>
            <Typography
              variant="body1"
              sx={{
                fontSize: qtyFont,
                fontWeight: large ? 'bold' : undefined,
                padding: isPackageMode && !large ? '2px' : '4px',
                borderRadius: '4px',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.08)',
                }
              }}
              onClick={(e) => {
                e.stopPropagation(); // Ana ListItem tıklamasını engelle
                if (onQuantityClick) {
                  onQuantityClick(item);
                }
              }}
            >
              {item.quantity}
            </Typography>
          </Grid>
          <Grid item xs={2} sx={{ textAlign: 'right' }}>
            <Typography variant="body1" sx={{ fontSize: priceFont }}>
              {item.price.toFixed(2)}
            </Typography>
          </Grid>
          <Grid item xs={3} sx={{ textAlign: 'right' }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: totalFont }}>
              {/* ⚠️ TERMİNOLOJİ: 'included' = KDV dahil fiyat, 'excluded' = KDV hariç fiyat */}
              {item.vatStatus === 'included'
                ? (item.price * item.quantity).toFixed(2)
                : ((item.price * item.quantity) * (1 + (item.vatRate || 0) / 100)).toFixed(2)}{isPackageMode && !large ? '' : ' TL'}
            </Typography>
          </Grid>
        </Grid>
      </ListItem>
    );
  };

  return (
    <Paper elevation={1} sx={{
      display: 'flex',
      flexDirection: 'column',
      p: 1,
      bgcolor: 'background.paper',
      height: '100%', // Parent'tan yüksekliği al (flex container'dan)
      minHeight: 0, // ✅ Flex child'ın küçülmesine izin ver - scroll için gerekli
      overflow: 'hidden', // Taşmaları engelle
    }}>
      {/* Sepet Başlığı - Mobilde butonlarla */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', fontSize: '1rem' }}>
            <ShoppingCartIcon sx={{ mr: 1, fontSize: '1.2rem' }} /> {t('cartSection.cart')}
          </Typography>

          {/* ✅ Sepetteki farklı kalem (ürün) sayısı — başlığın hemen sağında.
              Sayım `cart` propu üzerinden yapılır; bölünmüş sepet (cart1/cart2) durumunda
              dışarıdan zaten o anda görüntülenen sepet `cart` olarak geliyor, bu yüzden
              gösterilen sayı her zaman ekrandaki sepete aittir. */}
          <Chip
            size="small"
            label={t('cartSection.itemCount', { count: cart.length })}
            color={cart.length > 0 ? 'primary' : 'default'}
            variant={cart.length > 0 ? 'filled' : 'outlined'}
            sx={{
              height: 22,
              fontSize: '0.72rem',
              fontWeight: 600,
              '& .MuiChip-label': { px: 1 },
            }}
          />

          {/* ✅ YENİ: Sepet bölünmüşse dropdown göster */}
          {hasSplitCart && (
            <FormControl size="small" sx={{ minWidth: 90 }}>
              <Select
                value={selectedCartView}
                onChange={handleCartViewChange}
                variant="outlined"
                sx={{
                  height: 28,
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  '& .MuiSelect-select': {
                    py: 0.5,
                    px: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  },
                  bgcolor: selectedCartView === 'cart2' ? 'secondary.light' : 'primary.light',
                  color: 'white',
                  '& .MuiSvgIcon-root': {
                    color: 'white',
                  },
                }}
              >
                <MenuItem value="cart1">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CallSplitIcon sx={{ fontSize: '0.9rem' }} />
                    {t('cartSection.cart1')}
                  </Box>
                </MenuItem>
                <MenuItem value="cart2">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CallSplitIcon sx={{ fontSize: '0.9rem' }} />
                    {t('cartSection.cart2')}
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {/* ✅ YENİ: "Tümü" — sepetin tamamını uzun/dar bir modalda gösterir.
              Büyük çözünürlüklü ekranlarda panele bir iki satır sığdığı için
              garsonlar sepeti göremiyordu. */}
          <Button
            size="small"
            variant="outlined"
            startIcon={<ListAltIcon />}
            onClick={() => setAllItemsOpen(true)}
            disabled={displayedCart.length === 0}
            sx={{
              fontSize: '0.75rem',
              py: 0.25,
              px: 1.25,
              minWidth: 'auto',
              fontWeight: 'bold',
              '& .MuiButton-startIcon': { mr: 0.5 },
            }}
          >
            {t('cartSection.showAll')}
          </Button>

          {/* Mobil Butonlar - Sadece mobilde göster */}
          {isMobile && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Button 
                size="small" 
                variant="contained"
                onClick={() => setProductsDrawerOpen(true)}
                sx={{ 
                  bgcolor: 'primary.main', 
                  color: 'white',
                  fontSize: '0.75rem',
                  py: 0.5,
                  px: 1.5,
                  minWidth: 'auto',
                  fontWeight: 'bold',
                  '&:hover': { bgcolor: 'primary.dark' }
                }}
              >
                {t('cartSection.products')}
              </Button>
              <Button 
                size="small" 
                variant="contained"
                onClick={() => setButtonsDrawerOpen(true)}
                sx={{ 
                  bgcolor: 'secondary.main', 
                  color: 'white',
                  fontSize: '0.75rem',
                  py: 0.5,
                  px: 1.5,
                  minWidth: 'auto',
                  fontWeight: 'bold',
                  '&:hover': { bgcolor: 'secondary.dark' }
                }}
              >
                {t('cartSection.actions')}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
      <Divider sx={{ mb: 0.5 }} />

      {/* ✅ PAKET MODU: Toplam ve Buton ÜSTTE göster (modal içinde görünür olması için) */}
      {isPackageMode && (
        <Box sx={{ mb: 0.5, flexShrink: 0 }}>
          {/* Toplam */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            bgcolor: 'primary.50',
            px: 1,
            py: 0.5,
            borderRadius: 1,
            mb: 0.5,
            border: '1px solid',
            borderColor: 'primary.main'
          }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              {t('cartSection.totalLabel')}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              {displayedTotal.toFixed(2)} ₺
            </Typography>
          </Box>
          
          {/* Sepeti Onayla Butonu */}
          <Button
            fullWidth
            variant="contained"
            color="primary"
            size="small"
            sx={{ 
              height: '36px', 
              fontSize: '0.85rem',
              fontWeight: 'bold',
              borderRadius: 1,
              '&.Mui-disabled': {
                bgcolor: 'grey.400',
                color: 'grey.600',
              }
            }}
            onClick={onConfirmPackageCart}
            disabled={cart.length === 0 || !isPackageCustomerReady}
          >
            {!isPackageCustomerReady ? t('cartSection.loading') : t('cartSection.confirmCart')}
          </Button>
        </Box>
      )}

      {/* ✅ YENİ: Okutulduğu hâlde sepete eklenemeyen ürünlerin uyarısı.
          Sepet listesinin hemen üstünde durur ki kasiyer sepete her bakışında görsün. */}
      <ScanAlertBanner
        alerts={scanAlerts}
        onOpen={() => onOpenScanAlerts?.()}
        onClearAll={() => onClearScanAlerts?.()}
      />

      <Box sx={{
        flexGrow: 1, // Kalan alanı kaplasın
        overflowY: 'auto',
        my: 0.5,
        border: '1px solid #eee',
        borderRadius: 1,
        minHeight: isPackageMode ? 100 : 0, // Paket modunda minimum yükseklik
      }}>
        <List dense sx={{ py: 0 }}>
          {renderCartTableHeader()}

          {displayedCart.length === 0 ? (
            <Typography sx={{ textAlign: 'center', p: 3, color: 'text.secondary', fontSize: '1rem' }}>
              {hasSplitCart && selectedCartView === 'cart2' ? t('cartSection.emptyCart2') : t('cartSection.empty')}
            </Typography>
          ) : (
            displayedCart.map((item, index) => renderCartRow(item, index))
          )}
        </List>
      </Box>

      {/* ✅ PAKET MODUNDA GİZLE - Toplam ve buton üstte gösteriliyor */}
      {!isPackageMode && (
      <Box sx={{ mt: 'auto', flexShrink: 0 }}>
        <Divider sx={{ mb: 1 }} />
        {lastAppliedDiscountAmount > 0 && (
          <Typography variant="body2" color="error" sx={{ textAlign: 'right', fontWeight: 'bold', mb: 0.5 }}>
            Uygulanan İndirim: -{formatCurrency(lastAppliedDiscountAmount)}
          </Typography>
        )}
        <Grid container alignItems="center">
          {/* ✅ YENİ: Restoran modunda Geçmiş butonu */}
          {isRestaurantMode && (
            <Grid item xs="auto" sx={{ pr: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<HistoryIcon />}
                onClick={() => setHistoryModalOpen(true)}
                sx={{ 
                  minWidth: 'auto',
                  px: 1.5,
                  py: 0.5,
                  fontSize: '0.75rem',
                  borderColor: 'grey.400',
                  color: 'grey.700',
                  '&:hover': {
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    bgcolor: 'primary.light',
                  }
                }}
              >
                {t('cartSection.history')}
              </Button>
            </Grid>
          )}
          {showReceiptNumber && (
            <Grid item xs={isRestaurantMode ? 'auto' : 6} sx={{ pr: 1 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: 'medium', 
                  fontSize: '0.9rem',
                  color: 'text.secondary'
                }}
              >
                {t('cartSection.receiptNo', { number: nextReceiptNumber })}
              </Typography>
            </Grid>
          )}
          <Grid item xs>
            <Typography
              variant="h6"
              align="right"
              sx={{
                fontWeight: 'bold',
                fontSize: '1.8rem',
                color: hasSplitCart && selectedCartView === 'cart2' ? 'secondary.main' : 'primary.main'
              }}
            >
              {isMobile ? `${displayedTotal.toFixed(2)} TL` : t('cartSection.totalWithAmount', { amount: displayedTotal.toFixed(2) })}
            </Typography>
          </Grid>
        </Grid>

        <Box sx={{ pt: 1 }}>
          <Grid container spacing={1}>
            {isPackageMode ? (
              // ✅ PAKET MODU: Sadece Sepeti Onayla butonu göster
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  size="large"
                  sx={{ 
                    height: '70px', 
                    fontSize: '1.3rem',
                    fontWeight: 'bold',
                    borderRadius: 2,
                    boxShadow: 3,
                    '&:hover': {
                      boxShadow: 5,
                    },
                    '&.Mui-disabled': {
                      bgcolor: 'grey.400',
                      color: 'grey.600',
                    }
                  }}
                  onClick={onConfirmPackageCart}
                  disabled={cart.length === 0 || !isPackageCustomerReady}
                >
                  {!isPackageCustomerReady ? t('cartSection.customerLoading') : t('cartSection.confirmCart')}
                </Button>
              </Grid>
            ) : prepaidSystemEnabled ? (
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  color="success"
                  size="medium"
                  sx={{ height: '50px', fontSize: '0.9rem', fontWeight: 'bold' }}
                  onClick={handleCompleteTransactionInPrepaidMode}
                  disabled={isPurchaseProcessing || isProcessingPayment || cart.length === 0 || !prepaidCard || (calculateTotal() > 0 && !selectedPrepaidWalletId)}
                >
                  {isPurchaseProcessing ? <CircularProgress size={24} color="inherit" /> : t('cartSection.completeTransaction')}
                </Button>
              </Grid>
            ) : isRestaurantMode ? (
              // ✅ RESTAURANT MODU: Yetkilere göre buton göster
              <>
                {/* ✅ ÜST SATIR: Adisyon İptal | Adisyon Yazdır | Mutfağa Yazdır */}
                {(canCancelReceiptEffective || waiterCanPrintReceipt || waiterCanPrintToKitchen) ? (
                  <>
                    {/* Adisyon İptal butonu - canCancelReceipt VEYA (canCancelUnprintedReceipt + masa yazdırılmamış) */}
                    {canCancelReceiptEffective && (
                      <Grid item xs={waiterCanPrintReceipt && waiterCanPrintToKitchen ? 4 : waiterCanPrintReceipt || waiterCanPrintToKitchen ? 6 : 12}>
                        <Button
                          fullWidth
                          variant="contained"
                          color="warning"
                          size="medium"
                          sx={{ height: '50px', fontSize: '0.75rem', fontWeight: 'bold' }}
                          onClick={handleOpenCancelCheckConfirm}
                          disabled={isClosingTable || cart.length === 0 || pavoP2pPaymentPending}
                        >
                          {isClosingTable ? <CircularProgress size={24} color="inherit" /> : t('cartSection.buttons.cancelCheck')}
                        </Button>
                      </Grid>
                    )}
                    {/* Adisyon Yazdır butonu - waiterCanPrintReceipt yetkisine bağlı */}
                    {waiterCanPrintReceipt && (
                      <Grid item xs={canCancelReceiptEffective && waiterCanPrintToKitchen ? 4 : canCancelReceiptEffective || waiterCanPrintToKitchen ? 6 : 12}>
                        <Button
                          fullWidth
                          variant="contained"
                          color="secondary"
                          size="medium"
                          sx={{ height: '50px', fontSize: '0.75rem', fontWeight: 'bold' }}
                          onClick={() => handleOpenPrintConfirm('check')}
                          disabled={cart.length === 0}
                        >
                          {t('cartSection.buttons.printCheck')}
                        </Button>
                      </Grid>
                    )}
                    {/* Mutfağa Yazdır butonu - waiterCanPrintToKitchen yetkisine bağlı */}
                    {waiterCanPrintToKitchen && (
                      <Grid item xs={canCancelReceiptEffective && waiterCanPrintReceipt ? 4 : canCancelReceiptEffective || waiterCanPrintReceipt ? 6 : 12}>
                        <Button
                          fullWidth
                          variant="contained"
                          color="info"
                          size="medium"
                          sx={{ height: '50px', fontSize: '0.75rem', fontWeight: 'bold' }}
                          onClick={() => handleOpenPrintConfirm('kitchen')}
                          disabled={cart.length === 0}
                        >
                          {t('cartSection.buttons.printKitchen')}
                        </Button>
                      </Grid>
                    )}
                  </>
                ) : null}
                
                {waiterCanCloseSale ? (
                  pavoP2pPaymentPending ? (
                    <Grid item xs={12}>
                      <Box sx={{ mt: 1, p: 1.5, bgcolor: '#7c4dff22', borderRadius: 1, border: '1px solid #7c4dff', textAlign: 'center' }}>
                        <Typography variant="body2" sx={{ color: '#7c4dff', fontWeight: 600 }}>
                          Bu masanın ödemesi Pavo'ya gönderildi, lütfen bekleyin.
                        </Typography>
                      </Box>
                    </Grid>
                  ) : (
                  <>
                    <Grid item xs={showOpenAccountButton ? 4 : 6}>
                      <Button
                        fullWidth
                        variant="contained"
                        color="success"
                        size="medium"
                        sx={{ height: '50px', fontSize: showOpenAccountButton ? '0.75rem' : '0.9rem', mt: (canCancelReceiptEffective || waiterCanPrintReceipt || waiterCanPrintToKitchen) ? 1 : 0, position: 'relative' }}
                        onClick={handleCashPaymentForQuickSale}
                        // ℹ️ Rest-P2P offline iken de tıklanabilir: akış başlar,
                        // PavoRestP2PWaitModal "Yine de Devam Et" sunar (mali fişsiz SQL kaydı).
                        disabled={isProcessingPayment || cart.length === 0}
                      >
                        {isProcessingPayment ? <CircularProgress size={24} color="inherit" /> : cashPaymentLabel}
                        {isRestP2PEnabled && useFiscalDeviceForCash && (
                          <Box sx={{ position: 'absolute', top: 6, right: 6, display: 'flex' }}>
                            <PavoLiveBadge
                              deviceCode={restP2PDeviceCode}
                              deviceName={restP2PDeviceName}
                              size="small"
                              variant="dot"
                              onOnlineChange={setRestP2POnline}
                            />
                          </Box>
                        )}
                      </Button>
                    </Grid>
                    <Grid item xs={showOpenAccountButton ? 4 : 6}>
                      <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        size="medium"
                        sx={{ height: '50px', fontSize: showOpenAccountButton ? '0.75rem' : '0.9rem', mt: (canCancelReceiptEffective || waiterCanPrintReceipt || waiterCanPrintToKitchen) ? 1 : 0, position: 'relative' }}
                        onClick={handlePosButtonClick}
                        // ℹ️ Rest-P2P offline iken de tıklanabilir: akış başlar,
                        // PavoRestP2PWaitModal "Yine de Devam Et" sunar (mali fişsiz SQL kaydı).
                        disabled={isProcessingPayment || cart.length === 0}
                      >
                        {isProcessingPayment && singlePosOption ? <CircularProgress size={24} color="inherit" /> : (singlePosOption ? singlePosOption.label : 'POS')}
                        {isRestP2PEnabled && (
                          <Box sx={{ position: 'absolute', top: 6, right: 6, display: 'flex' }}>
                            <PavoLiveBadge
                              deviceCode={restP2PDeviceCode}
                              deviceName={restP2PDeviceName}
                              size="small"
                              variant="dot"
                              onOnlineChange={setRestP2POnline}
                            />
                          </Box>
                        )}
                      </Button>
                    </Grid>
                    {showOpenAccountButton && (
                      <Grid item xs={4}>
                        <Button
                          fullWidth
                          variant="contained"
                          size="medium"
                          sx={{ height: '50px', fontSize: '0.75rem', mt: (canCancelReceiptEffective || waiterCanPrintReceipt || waiterCanPrintToKitchen) ? 1 : 0, bgcolor: '#ff9800', '&:hover': { bgcolor: '#f57c00' } }}
                          onClick={handleOpenAccountPayment}
                          disabled={isProcessingPayment || cart.length === 0}
                        >
                          {isProcessingPayment ? <CircularProgress size={24} color="inherit" /> : t('cartSection.buttons.openAccount')}
                        </Button>
                      </Grid>
                    )}
                  </>
                  )
                ) : null}
                
                {!waiterCanCloseSale && !canCancelReceiptEffective && !waiterCanPrintReceipt && !waiterCanPrintToKitchen && (
                  // Hiçbir yetki yoksa uyarı göster
                  <Grid item xs={12}>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="error"
                      size="large"
                      disabled
                      sx={{ height: '60px', fontSize: '0.9rem' }}
                    >
                      {t('cartSection.buttons.noPermission')}
                    </Button>
                  </Grid>
                )}
              </>
            ) : (
              // Normal mod: Nakit/POS butonları + Açık Hesap (koşullu)
              <>
                <Grid item xs={showOpenAccountButton ? 4 : 6}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="success"
                    size="medium"
                    sx={{ height: '50px', fontSize: showOpenAccountButton ? '0.75rem' : '0.9rem', position: 'relative' }}
                    onClick={handleCashPaymentForQuickSale}
                    // ℹ️ Rest-P2P offline iken de tıklanabilir: akış başlar,
                    // PavoRestP2PWaitModal "Yine de Devam Et" sunar (mali fişsiz SQL kaydı).
                    disabled={isProcessingPayment || cart.length === 0 || pavoP2pPaymentPending}
                  >
                    {isProcessingPayment ? <CircularProgress size={24} color="inherit" /> : cashPaymentLabel}
                    {isRestP2PEnabled && useFiscalDeviceForCash && (
                      <Box sx={{ position: 'absolute', top: 6, right: 6, display: 'flex' }}>
                        <PavoLiveBadge
                          deviceCode={restP2PDeviceCode}
                          deviceName={restP2PDeviceName}
                          size="small"
                          variant="dot"
                          onOnlineChange={setRestP2POnline}
                        />
                      </Box>
                    )}
                  </Button>
                </Grid>
                <Grid item xs={showOpenAccountButton ? 4 : 6}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    size="medium"
                    sx={{ height: '50px', fontSize: showOpenAccountButton ? '0.75rem' : '0.9rem', position: 'relative' }}
                    onClick={handlePosButtonClick}
                    // ℹ️ Rest-P2P offline iken de tıklanabilir: akış başlar,
                    // PavoRestP2PWaitModal "Yine de Devam Et" sunar (mali fişsiz SQL kaydı).
                    disabled={isProcessingPayment || cart.length === 0 || pavoP2pPaymentPending}
                  >
                    {isProcessingPayment && singlePosOption ? <CircularProgress size={24} color="inherit" /> : (singlePosOption ? singlePosOption.label : 'POS')}
                    {isRestP2PEnabled && (
                      <Box sx={{ position: 'absolute', top: 6, right: 6, display: 'flex' }}>
                        <PavoLiveBadge
                          deviceCode={restP2PDeviceCode}
                          deviceName={restP2PDeviceName}
                          size="small"
                          variant="dot"
                          onOnlineChange={setRestP2POnline}
                        />
                      </Box>
                    )}
                  </Button>
                </Grid>
                {showOpenAccountButton && (
                  <Grid item xs={4}>
                    <Button
                      fullWidth
                      variant="contained"
                      size="medium"
                      sx={{ height: '50px', fontSize: '0.75rem', bgcolor: '#ff9800', '&:hover': { bgcolor: '#f57c00' } }}
                      onClick={handleOpenAccountPayment}
                      disabled={isProcessingPayment || cart.length === 0 || pavoP2pPaymentPending}
                    >
                      {isProcessingPayment ? <CircularProgress size={24} color="inherit" /> : t('cartSection.buttons.openAccount')}
                    </Button>
                  </Grid>
                )}
              </>
            )}
            {/* PavoPay P2P Ödeme Butonu */}
            {handlePavoP2pPayment && (
              <Grid item xs={12}>
                <Button 
                  fullWidth 
                  variant="contained" 
                  size="small" 
                  sx={{ mt: 1, bgcolor: pavoP2pPaymentPending ? '#9C27B0' : '#7c4dff', '&:hover': { bgcolor: pavoP2pPaymentPending ? '#7B1FA2' : '#651fff' } }} 
                  disabled={isProcessingPayment}
                  onClick={handlePavoP2pPayment}
                >
                  {isProcessingPayment ? <CircularProgress size={20} color="inherit" /> : pavoP2pPaymentPending ? t('cartSection.buttons.pavoPaymentStatus') : t('cartSection.buttons.pavoPayP2p')}
                </Button>
              </Grid>
            )}
            {/* Diğer Ödeme ve Online Ödeme Butonları - Sadece tanımlıysa göster (Paket modunda gizle) */}
            {!isPackageMode && !prepaidSystemEnabled && hasOtherPaymentOptions && (
              <Grid item xs={hasOnlinePaymentOptions ? 6 : 12}>
                <Button 
                  fullWidth 
                  variant="outlined" 
                  color="warning" 
                  size="small" 
                  sx={{ mt: 1 }} 
                  disabled={cart.length === 0 || pavoP2pPaymentPending}
                  onClick={onOpenOtherPaymentModal}
                >
                  {t('cartSection.buttons.otherPayment')}
                </Button>
              </Grid>
            )}
            {!isPackageMode && hasOnlinePaymentOptions && (
              <Grid item xs={hasOtherPaymentOptions ? 6 : 12}>
                <Button 
                  fullWidth 
                  variant="outlined" 
                  color="info" 
                  size="small" 
                  sx={{ mt: 1 }} 
                  onClick={onOpenOnlinePaymentModal}
                  disabled={cart.length === 0 || pavoP2pPaymentPending}
                >
                  {t('cartSection.buttons.onlinePayment')}
                </Button>
              </Grid>
            )}
          </Grid>
        </Box>
      </Box>
      )}

      {/* ✅ YENİ: "Tümü" — sepetin tamamı. Uzun ve dar (maxWidth="sm") bir modal:
          büyük ekranlarda sepet paneline sığmayan satırlar burada büyük yazıyla
          okunur. Tablo yapısı panelle birebir aynı (renderCartTableHeader/renderCartRow). */}
      <Dialog
        open={allItemsOpen}
        onClose={() => setAllItemsOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { height: '95vh', maxHeight: '95vh', m: 1 } }}
        aria-labelledby="cart-all-items-dialog-title"
      >
        <DialogTitle id="cart-all-items-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.25 }}>
          <ShoppingCartIcon />
          <span>{t('cartSection.allItemsDialog.title')}</span>
          <Chip
            size="small"
            label={t('cartSection.itemCount', { count: displayedCart.length })}
            color="primary"
            sx={{ fontWeight: 600 }}
          />
          <IconButton onClick={() => setAllItemsOpen(false)} sx={{ ml: 'auto' }} aria-label={t('cartSection.allItemsDialog.close')}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <List dense sx={{ py: 0 }}>
            {renderCartTableHeader(true)}
            {displayedCart.length === 0 ? (
              <Typography sx={{ textAlign: 'center', p: 3, color: 'text.secondary', fontSize: '1.1rem' }}>
                {hasSplitCart && selectedCartView === 'cart2' ? t('cartSection.emptyCart2') : t('cartSection.empty')}
              </Typography>
            ) : (
              displayedCart.map((item, index) => renderCartRow(item, index, true))
            )}
          </List>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 2, py: 1.25 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.5rem', color: hasSplitCart && selectedCartView === 'cart2' ? 'secondary.main' : 'primary.main' }}>
            {t('cartSection.totalWithAmount', { amount: displayedTotal.toFixed(2) })}
          </Typography>
          <Button variant="contained" onClick={() => setAllItemsOpen(false)}>
            {t('cartSection.allItemsDialog.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sepeti Temizleme Onay Dialogu */}
      <Dialog
        open={clearCartConfirmOpen}
        onClose={handleCloseClearCartConfirm}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t('cartSection.clearCartDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t('cartSection.clearCartDialog.description')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseClearCartConfirm} color="primary">
            {t('cartSection.clearCartDialog.cancel')}
          </Button>
          <Button onClick={onConfirmClearCart} color="error" autoFocus>
            {t('cartSection.clearCartDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ YENİ: Adisyon Yazdır / Mutfağa Yazdır Onay Modalı (Restaurant Modu) */}
      <Dialog
        open={printConfirmType !== null}
        onClose={handleClosePrintConfirm}
        aria-labelledby="print-confirm-dialog-title"
        aria-describedby="print-confirm-dialog-description"
      >
        <DialogTitle id="print-confirm-dialog-title">
          {printConfirmType === 'kitchen'
            ? t('cartSection.printConfirmDialog.kitchenTitle')
            : t('cartSection.printConfirmDialog.checkTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="print-confirm-dialog-description">
            {printConfirmType === 'kitchen'
              ? t('cartSection.printConfirmDialog.kitchenDescription', { tableName: tableName || '' })
              : t('cartSection.printConfirmDialog.checkDescription', { tableName: tableName || '' })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePrintConfirm} color="inherit">
            {t('cartSection.printConfirmDialog.cancel')}
          </Button>
          <Button
            onClick={onConfirmPrint}
            variant="contained"
            color={printConfirmType === 'kitchen' ? 'info' : 'secondary'}
            autoFocus
          >
            {t('cartSection.printConfirmDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ YENİ: Adisyon İptal Onay Modalı (Restaurant Modu) */}
      {isRestaurantMode && (
        <>
          <ConfirmCancelCheckModal
            open={cancelCheckConfirmOpen}
            tableName={tableName}
            waiterName={waiterName}
            itemCount={cart.length}
            totalAmount={calculateTotal() - lastAppliedDiscountAmount}
            onConfirm={onConfirmCancelCheck}
            onCancel={handleCloseCancelCheckConfirm}
            isProcessing={isClosingTable}
          />
          <DeleteReasonModal
            open={cancellationReasonModalOpen}
            productName={`${tableName} - ${cart.length} ürün`}
            title={t('cartSection.deleteReasonModal.title')}
            subtitle={t('cartSection.deleteReasonModal.subtitle')}
            inputLabel={t('cartSection.deleteReasonModal.inputLabel')}
            confirmButtonLabel={t('cartSection.deleteReasonModal.confirm')}
            headerColor="warning.main"
            predefinedReasons={[
              t('cartSection.deleteReasonModal.reasons.customerCancelled'),
              t('cartSection.deleteReasonModal.reasons.wrongOrder'),
              t('cartSection.deleteReasonModal.reasons.tableChanged'),
              t('cartSection.deleteReasonModal.reasons.staffError')
            ]}
            onConfirm={onConfirmCancellationReason}
            onCancel={onCancelCancellationReason}
          />
        </>
      )}

      {/* ÜRÜNLER DRAWER - Alttan yukarı açılır (Bottom Sheet) */}
      <SwipeableDrawer
        anchor="bottom"
        open={productsDrawerOpen}
        onClose={() => setProductsDrawerOpen(false)}
        onOpen={() => setProductsDrawerOpen(true)}
        disableSwipeToOpen={false}
        swipeAreaWidth={20}
        ModalProps={{
          keepMounted: true, // Performans için
        }}
        PaperProps={{
          sx: {
            height: '80vh', // Ekranın %80'i kadar yükseklik
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            bgcolor: 'background.paper',
            overflow: 'hidden',
          }
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Tutamak (Handle) - Parmakla sürüklemek için */}
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              py: 1.5,
              cursor: 'grab',
              '&:active': { cursor: 'grabbing' }
            }}
            onClick={() => setProductsDrawerOpen(false)}
          >
            <Box 
              sx={{ 
                width: 40, 
                height: 5, 
                bgcolor: 'grey.400', 
                borderRadius: 3,
              }} 
            />
          </Box>
          
          {/* Başlık */}
          <Box sx={{ 
            px: 2, 
            pb: 1.5, 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Ürünler
            </Typography>
            <IconButton 
              size="small" 
              onClick={() => setProductsDrawerOpen(false)}
              sx={{ color: 'text.secondary' }}
            >
              <KeyboardArrowDownIcon />
            </IconButton>
          </Box>
          
          {/* İçerik - ProductDisplayArea buraya gelecek */}
          <Box sx={{ flexGrow: 1, overflow: 'auto', p: 1 }}>
            {mobileProductsContent || (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary" align="center">
                  Ürün kategorileri ve ürün kartları burada gösterilecek.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </SwipeableDrawer>

      {/* BUTONLAR DRAWER - Alttan yukarı açılır (Bottom Sheet) */}
      <SwipeableDrawer
        anchor="bottom"
        open={buttonsDrawerOpen}
        onClose={() => setButtonsDrawerOpen(false)}
        onOpen={() => setButtonsDrawerOpen(true)}
        disableSwipeToOpen={false}
        swipeAreaWidth={20}
        ModalProps={{
          keepMounted: true, // Performans için
        }}
        PaperProps={{
          sx: {
            height: '60vh', // İşlemler için daha kısa
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            bgcolor: 'background.paper',
            overflow: 'hidden',
          }
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Tutamak (Handle) - Parmakla sürüklemek için */}
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              py: 1.5,
              cursor: 'grab',
              '&:active': { cursor: 'grabbing' }
            }}
            onClick={() => setButtonsDrawerOpen(false)}
          >
            <Box 
              sx={{ 
                width: 40, 
                height: 5, 
                bgcolor: 'grey.400', 
                borderRadius: 3,
              }} 
            />
          </Box>
          
          {/* Başlık */}
          <Box sx={{ 
            px: 2, 
            pb: 1.5, 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              İşlemler & Numpad
            </Typography>
            <IconButton 
              size="small" 
              onClick={() => setButtonsDrawerOpen(false)}
              sx={{ color: 'text.secondary' }}
            >
              <KeyboardArrowDownIcon />
            </IconButton>
          </Box>
          
          {/* İçerik - ActionControlsPanel buraya gelecek */}
          <Box sx={{ flexGrow: 1, overflow: 'auto', p: 1 }}>
            {mobileButtonsContent || (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary" align="center">
                  İşlem butonları ve numpad burada gösterilecek.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </SwipeableDrawer>

      {/* Ödeme İşlemi Bekleniyor Modalı - Sadece POS için */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: 9999, // ✅ En üstte çıksın (modal'ların üzerinde)
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          backgroundColor: 'rgba(25, 118, 210, 0.95)', // Mavi arka plan (hafif şeffaf)
        }}
        open={isProcessingPosPayment} // Sadece POS işlemlerinde açılır
      >
        <CircularProgress color="inherit" size={80} thickness={5} />
        <Typography variant="h3" sx={{ fontWeight: 700, textAlign: 'center', textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}>
          Ödeme Bekleniyor
        </Typography>
        <Typography variant="h5" sx={{ textAlign: 'center', maxWidth: 500, fontWeight: 500, textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}>
          Lütfen POS cihazından işlemi tamamlayın
        </Typography>
      </Backdrop>

      {/* ✅ YENİ: Garson İşlemleri Geçmişi Modalı */}
      {isRestaurantMode && (
        <WaiterActionsHistoryModal
          open={historyModalOpen}
          onClose={() => setHistoryModalOpen(false)}
          waiterActionsJson={waiterActionsJson}
          tableName={tableName}
          tableId={tableId}
        />
      )}
    </Paper>
  );
};

export default CartSection; 
